// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Header from "./components/Header";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import Loading from "./components/Loading";
import {
  clearAuthState,
  isAuthenticated as checkAuth,
  isTokenExpired,
} from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";
import AuthGuard from "./components/AuthGuard";
import ThemeBootstrapper from "./components/ThemeBootstrapper";
import CssBaseline from "@mui/material/CssBaseline";

// Lazy-loaded components
const Dashboard = lazy(() => import("./components/Dashboard"));
const Startup = lazy(() => import("./components/Startup"));
const CostReportsPage = lazy(() => import("./components/CostReportsPage"));
const AwsCostTracking = lazy(() => import("./components/AwsCostTracking"));
const RegressionDashboardPage = lazy(
  () => import("./components/RegressionDashboardPage"),
);
const HelpPage = lazy(() => import("./components/HelpPage"));
const HelpArticlePage = lazy(() => import("./components/help/HelpArticlePage"));
const AnalyticsPage = lazy(() => import("./components/AnalyticsPage"));
const AnalyticsPage2 = lazy(() => import("./components/AnalyticsPage2"));
const AnalyticsPage3 = lazy(() => import("./components/AnalyticsPage3"));
const ConfiguratorPage = lazy(() => import("./components/ConfiguratorPage"));

// Component to prevent accidental swipe-back navigation (Safari's white circle arrow gesture)
// eslint-disable-next-line no-unused-vars -- intentionally not rendered (disabled); see note at former usage site
const SwipeNavigationBlocker = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = React.useRef(location.pathname);
  const isBlockingRef = React.useRef(false);

  useEffect(() => {
    currentPathRef.current = location.pathname;
    isBlockingRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const publicPages = ["/login", "/register", "/confirm-signup"];
    const isOnPublicPage = publicPages.some(
      (page) => location.pathname === page,
    );

    if (isOnPublicPage) return;

    const handlePopState = (event) => {
      if (isBlockingRef.current) return;

      const targetPath = currentPathRef.current;
      const currentUrl = window.location.pathname;

      if (currentUrl !== targetPath) {
        isBlockingRef.current = true;
        window.history.replaceState(null, "", targetPath);
        navigate(targetPath, { replace: true });
        console.log(
          "Prevented accidental back navigation from Safari swipe gesture",
        );
        setTimeout(() => {
          isBlockingRef.current = false;
        }, 100);
      }
    };

    window.history.pushState({ preventBack: true }, "", currentPathRef.current);
    window.addEventListener("popstate", handlePopState, { passive: true });

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      // Only honor a saved personal theme for AUTHENTICATED users; unauth visitors
      // get the brand default (refined by the no-store /site-theme.json fetch) so a
      // stale localStorage theme can never flash on first paint.
      const token = localStorage.getItem("jwtToken");
      const isAuthed = token && !isTokenExpired(token);
      if (isAuthed) {
        const savedTheme = localStorage.getItem("THEME");
        if (savedTheme) return JSON.parse(savedTheme);
      }
    } catch (e) {
      console.error("App.js - Error parsing saved theme:", e);
    }
    return themes.midnight;
  });

  // Fetch the site owner's selected theme unconditionally on startup.
  useEffect(() => {
    fetch("/site-theme.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => {
        if (t && t.primaryColor) handleThemeChange(t);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("App.js - Checking authentication state");

    const cleanup = preventAWSRedirects();

    const existingToken = localStorage.getItem("jwtToken");
    if (existingToken && !isTokenExpired(existingToken)) {
      console.log(
        "App.js - Valid session found, restoring authenticated state",
      );
      setIsAuthenticated(true);
    } else {
      clearAllAWSState();
      clearAuthState();
      setIsAuthenticated(false);
    }

    return cleanup;
  }, []);

  // Token expiration and inactivity timeout monitoring
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkInterval = setInterval(() => {
      const token = localStorage.getItem("jwtToken");

      if (token && isTokenExpired(token)) {
        console.warn("App.js - Token expired, logging out user");
        clearAuthState();
        setIsAuthenticated(false);
        window.location.replace("/login");
        return;
      }

      const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
      const lastActivityTime = localStorage.getItem("lastActivityTime");

      if (lastActivityTime) {
        const timeSinceLastActivity =
          Date.now() - parseInt(lastActivityTime, 10);

        if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          console.warn("App.js - Inactivity timeout reached, logging out user");
          clearAuthState();
          setIsAuthenticated(false);
          window.location.replace("/login");
          return;
        }
      }
    }, 30000);

    const updateActivityTime = () => {
      if (isAuthenticated) {
        localStorage.setItem("lastActivityTime", Date.now().toString());
      }
    };

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    events.forEach((event) => {
      window.addEventListener(event, updateActivityTime, { passive: true });
    });

    return () => {
      clearInterval(checkInterval);
      events.forEach((event) => {
        window.removeEventListener(event, updateActivityTime);
      });
    };
  }, [isAuthenticated]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <CssBaseline />
            <ThemeBootstrapper
              isAuthenticated={isAuthenticated}
              onThemeLoaded={handleThemeChange}
            />
            {/* SwipeNavigationBlocker disabled: it blocked legitimate browser back-navigation and caused blank-page-on-back via bfcache. */}
            <AuthGuard>
              {/* Persistent themed backdrop — keeps the page background painted
                  during route swaps so the white body never flashes through. */}
              <div style={{ minHeight: "100vh", backgroundColor: theme.backgroundColor }}>
              <Header
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
              />
              <Suspense fallback={<Loading />}>
                <Routes>
                  {/* Public routes - accessible without authentication */}
                  <Route path="/" element={<Dashboard />} />
                  <Route
                    path="/login"
                    element={
                      <AuthForm setIsAuthenticated={setIsAuthenticated} />
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <AuthForm setIsAuthenticated={setIsAuthenticated} />
                    }
                  />
                  <Route path="/confirm-signup" element={<ConfirmSignUp />} />

                  {/* All other routes - require authentication or redirect to login */}
                  {isAuthenticated ? (
                    <>
                      <Route
                        path="/startup"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <Startup />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/cost-reports"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <CostReportsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/aws-costs"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <AwsCostTracking />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/analytics"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <AnalyticsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/analytics2"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <AnalyticsPage2 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/analytics3"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <AnalyticsPage3 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/regression"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <RegressionDashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/configurator"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <ConfiguratorPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/help"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <HelpPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/help/:slug"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <HelpArticlePage />
                          </ProtectedRoute>
                        }
                      />
                    </>
                  ) : (
                    /* If not authenticated, redirect ALL routes to login */
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  )}
                </Routes>
              </Suspense>
              </div>
            </AuthGuard>
          </DndProvider>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
